import { useParams, Navigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { Loader2 } from "lucide-react";
import PageHeader from "../components/PageHeader";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/Breadcrumbs";
import ParentPolicyClauses from "../components/ParentPolicyClauses";
import { api } from "../lib/api";

export default function Policy() {
    const { policy } = useParams();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);

    useEffect(() => {
        setLoading(true);
        setError(false);
        api.get(`/policies/${policy}`)
            .then((r) => setData(r.data))
            .catch(() => setError(true))
            .finally(() => setLoading(false));
    }, [policy]);

    if (loading) {
        return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#1E3A8A]" /></div>;
    }
    if (error || !data) return <Navigate to="/" replace />;

    return (
        <div data-testid={`policy-${policy}-page`}>
            <SEO
                title={`${data.title} — Anand Tutorials`}
                description={`${data.title} for Anand Tutorials home and online tuition services. Read our official ${data.title.toLowerCase()}.`}
                path={`/policies/${policy}`}
            />
            <Breadcrumbs items={[
                { name: "Policies", to: "/policies/terms" },
                { name: data.title },
            ]} />
            <PageHeader eyebrow={data.eyebrow || "Policy"} title={data.title} />
            <section className="py-16 bg-white">
                <div className="max-w-3xl mx-auto px-6 lg:px-8 space-y-6">
                    {/* Parent Policy — prominent 9-clause summary before detailed legal */}
                    {policy === "parent" && (
                        <div className="-mt-4 mb-10">
                            <div className="text-xs font-semibold uppercase tracking-[0.25em] text-[#3B82F6] mb-3">Quick Reference — 9 Key Terms</div>
                            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-6">Anand Tutorials — Parent Terms & Conditions</h2>
                            <ParentPolicyClauses />
                            <div className="mt-10 pt-8 border-t-2 border-slate-200">
                                <div className="text-xs font-semibold uppercase tracking-[0.25em] text-slate-500 mb-2">Detailed Legal Version</div>
                                <h3 className="font-heading text-xl sm:text-2xl font-bold text-[#0F172A]">Full Parent Policy (Legally Binding)</h3>
                                <p className="text-sm text-slate-500 mt-1">The complete enforceable version of the Parent Policy follows below.</p>
                            </div>
                        </div>
                    )}
                    {(data.sections || []).map((s, i) => (
                        <div key={i}>
                            <h2 className="font-heading text-xl font-semibold text-[#1E3A8A] mb-2">{s.heading}</h2>
                            <p className="text-slate-700 leading-relaxed whitespace-pre-line">{s.body}</p>
                        </div>
                    ))}
                    {data.updated_at && (
                        <p className="text-sm text-slate-500 italic pt-6 border-t border-slate-200">
                            Last updated: {new Date(data.updated_at).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}.
                            For any clarification, write to anandhometutorials@gmail.com.
                        </p>
                    )}
                </div>
            </section>
        </div>
    );
}
